import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { useCurrentUser } from '@/hooks';
import {
  Button,
  Card,
  Chip,
  PageHeaderLayout,
  Pagination,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@/ui';

import type { TypeformAdminFormSummary } from '@/store/api/typeformAdminApi';
import type { ChangeEvent, ReactNode, SyntheticEvent } from 'react';

import { useGetPulseAdminReportsQuery } from '@/store/api/pulseAdminApi';
import {
  useExportTypeformAdminSubmissionsMutation,
  useGetTypeformAdminFormsQuery,
  useGetTypeformAdminSubmissionsQuery,
  useImportAllTypeformAdminFormsMutation,
  useImportTypeformAdminFormMutation,
  useReclassifyTypeformAdminFormMutation,
  useSyncTypeformAdminCatalogMutation,
  useSyncTypeformAdminFormDefinitionMutation,
  useUpdateTypeformAdminFormCatalogMutation,
} from '@/store/api/typeformAdminApi';
import { isAdmin } from '@/types/user';

type TabKey = 'forms' | 'pulseReports' | 'submissions';

interface SubmissionFilters {
  company: string;
  formType: string;
  q: string;
  status: string;
}

const defaultFilters = (searchParams: URLSearchParams): SubmissionFilters => ({
  company: searchParams.get('company') ?? '',
  formType: searchParams.get('formType') ?? '',
  q: searchParams.get('q') ?? '',
  status: searchParams.get('status') ?? '',
});

const formatDateTime = (value?: string | null) => {
  if (!value) return 'Not available';

  return new Date(value).toLocaleString();
};

const statusColor = (status?: string | null) => {
  if (status === 'archived') return 'warning';
  return 'success';
};

const submissionStatusFilter = (
  value: string,
): 'active' | 'archived' | undefined => {
  if (value === 'active' || value === 'archived') return value;
  return undefined;
};

const FormsPanel = ({
  forms,
  formTypeDrafts,
  isImporting,
  isImportingAll,
  isLoading,
  isReclassifying,
  isSyncingCatalog,
  isSyncingDefinition,
  isUpdatingCatalog,
  onDraftChange,
  onImportAll,
  onImport,
  onReclassify,
  onSyncCatalog,
  onSyncDefinition,
  onTogglePulseReportable,
}: {
  forms: TypeformAdminFormSummary[];
  formTypeDrafts: Record<string, string>;
  isImporting: boolean;
  isImportingAll: boolean;
  isLoading: boolean;
  isReclassifying: boolean;
  isSyncingCatalog: boolean;
  isSyncingDefinition: boolean;
  isUpdatingCatalog: boolean;
  onDraftChange: (formId: string, value: string) => void;
  onImportAll: () => Promise<void>;
  onImport: (formId: string) => Promise<void>;
  onReclassify: (formId: string) => Promise<void>;
  onSyncCatalog: () => Promise<void>;
  onSyncDefinition: (formId: string) => Promise<void>;
  onTogglePulseReportable: (
    formId: string,
    nextValue: boolean,
  ) => Promise<void>;
}) => {
  if (isLoading) {
    return (
      <Card className="p-4">
        <Typography variant="body1">Loading forms…</Typography>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <Typography variant="h5">Forms</Typography>
          <Typography variant="body2">
            Sync the Typeform catalog, import historic responses, and manage
            local form interpretation.
          </Typography>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            disabled={isSyncingCatalog}
            size="small"
            variant="outlined"
            onClick={() => {
              onSyncCatalog();
            }}
          >
            Sync catalog
          </Button>
          <Button
            disabled={isImportingAll}
            size="small"
            variant="outlined"
            onClick={() => {
              onImportAll();
            }}
          >
            Import all
          </Button>
        </div>
      </div>

      <TableContainer className="mt-4">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Form</TableCell>
              <TableCell>Form type</TableCell>
              <TableCell>Typeform ID</TableCell>
              <TableCell>Submissions</TableCell>
              <TableCell>Mappings</TableCell>
              <TableCell>Unmapped</TableCell>
              <TableCell>Pulse API</TableCell>
              <TableCell>Latest submission</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {forms.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9}>No forms found.</TableCell>
              </TableRow>
            ) : (
              forms.map((form) => {
                const draft =
                  formTypeDrafts[form.typeformFormId] ?? form.formType;

                return (
                  <TableRow key={form.typeformFormId}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {form.formTitle ?? 'Untitled form'}
                        </span>
                        <span className="text-xs text-cement-500">
                          {form.typeformFormId}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={draft}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                          onDraftChange(form.typeformFormId, event.target.value)
                        }
                      />
                    </TableCell>
                    <TableCell>{form.typeformFormId}</TableCell>
                    <TableCell>{form.submissionCount}</TableCell>
                    <TableCell>
                      {form.hasMappings ? 'Ready' : 'Missing'}
                    </TableCell>
                    <TableCell>{form.unmappedCount}</TableCell>
                    <TableCell>
                      <Switch
                        checked={form.pulseReportable}
                        disabled={isUpdatingCatalog}
                        inputProps={{
                          'aria-label': `Pulse reportable for ${form.formTitle ?? form.typeformFormId}`,
                        }}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          onTogglePulseReportable(
                            form.typeformFormId,
                            event.target.checked,
                          );
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {formatDateTime(form.latestSubmissionAt)}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        <Link
                          className="no-underline"
                          to={`/coach/manage/typeform/forms/${form.typeformFormId}/mappings`}
                        >
                          <Button size="small" variant="outlined">
                            Mappings
                          </Button>
                        </Link>
                        <Button
                          disabled={isSyncingDefinition}
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            onSyncDefinition(form.typeformFormId);
                          }}
                        >
                          Sync definition
                        </Button>
                        <Button
                          disabled={isImporting || !draft.trim()}
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            onImport(form.typeformFormId);
                          }}
                        >
                          Import
                        </Button>
                        <Button
                          disabled={isReclassifying || !draft.trim()}
                          size="small"
                          onClick={() => {
                            onReclassify(form.typeformFormId);
                          }}
                        >
                          Reclassify
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

const TypeformAdminPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isUserLoading, user } = useCurrentUser();
  const [filters, setFilters] = useState<SubmissionFilters>(() =>
    defaultFilters(searchParams),
  );
  const [notice, setNotice] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [formTypeDrafts, setFormTypeDrafts] = useState<Record<string, string>>(
    {},
  );
  const activeTab = (searchParams.get('tab') as TabKey | null) ?? 'submissions';
  const page = Number(searchParams.get('page') ?? '1') || 1;

  useEffect(() => {
    setFilters(defaultFilters(searchParams));
  }, [searchParams]);

  const submissionsArgs =
    activeTab === 'submissions'
      ? {
          company: searchParams.get('company') ?? undefined,
          formType: searchParams.get('formType') ?? undefined,
          page,
          per: 25,
          q: searchParams.get('q') ?? undefined,
          status:
            (searchParams.get('status') as 'active' | 'archived' | null) ??
            undefined,
        }
      : skipToken;

  const {
    data: submissionsData,
    isFetching: isSubmissionsFetching,
    isLoading: isSubmissionsLoading,
  } = useGetTypeformAdminSubmissionsQuery(submissionsArgs);
  const {
    data: formsData,
    isFetching: isFormsFetching,
    isLoading: isFormsLoading,
  } = useGetTypeformAdminFormsQuery();
  const [exportSubmissions, { isLoading: isExporting }] =
    useExportTypeformAdminSubmissionsMutation();
  const [importForm, { isLoading: isImporting }] =
    useImportTypeformAdminFormMutation();
  const [importAllForms, { isLoading: isImportingAll }] =
    useImportAllTypeformAdminFormsMutation();
  const [reclassifyForm, { isLoading: isReclassifying }] =
    useReclassifyTypeformAdminFormMutation();
  const [syncCatalog, { isLoading: isSyncingCatalog }] =
    useSyncTypeformAdminCatalogMutation();
  const [syncDefinition, { isLoading: isSyncingDefinition }] =
    useSyncTypeformAdminFormDefinitionMutation();
  const [updateCatalog, { isLoading: isUpdatingCatalog }] =
    useUpdateTypeformAdminFormCatalogMutation();
  const {
    data: pulseReportsData,
    isFetching: isPulseReportsFetching,
    isLoading: isPulseReportsLoading,
  } = useGetPulseAdminReportsQuery(
    {
      page,
      per: 25,
    },
    {
      skip: activeTab !== 'pulseReports',
    },
  );

  useEffect(() => {
    if (user.id && !isAdmin(user)) {
      navigate('/coach/manage', { replace: true });
    }
  }, [navigate, user]);

  useEffect(() => {
    if (!formsData?.forms) return;

    setFormTypeDrafts((current) => {
      const next = { ...current };

      formsData.forms.forEach((form) => {
        if (!next[form.typeformFormId]) {
          next[form.typeformFormId] = form.formType;
        }
      });

      return next;
    });
  }, [formsData]);

  const formPageCount = useMemo(() => {
    if (!submissionsData) return 1;

    return Math.max(1, Math.ceil(submissionsData.total / submissionsData.per));
  }, [submissionsData]);

  const pulseReportPageCount = useMemo(() => {
    if (!pulseReportsData) return 1;

    return Math.max(
      1,
      Math.ceil(pulseReportsData.total / pulseReportsData.per),
    );
  }, [pulseReportsData]);

  const handleTabChange = (_event: SyntheticEvent, value: TabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    next.set('page', '1');
    setSearchParams(next);
  };

  const handleFilterChange =
    (field: keyof SubmissionFilters) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setFilters((current) => ({ ...current, [field]: value }));
    };

  const handleFilterSubmit = () => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'submissions');
    next.set('page', '1');

    (
      Object.entries(filters) as Array<[keyof SubmissionFilters, string]>
    ).forEach(([key, value]) => {
      if (value.trim()) {
        next.set(key, value.trim());
      } else {
        next.delete(key);
      }
    });

    setSearchParams(next);
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(nextPage));
    setSearchParams(next);
  };

  const handleExport = async () => {
    try {
      setErrorMessage('');
      const blob = await exportSubmissions({
        company: filters.company || undefined,
        formType: filters.formType || undefined,
        q: filters.q || undefined,
        status: submissionStatusFilter(filters.status),
      }).unwrap();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `typeform-interpretation-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      setNotice('Export started.');
    } catch (error) {
      console.error(error);
      setErrorMessage('Export failed. Check your filters and try again.');
    }
  };

  const handleDraftChange = (formId: string, value: string) => {
    setFormTypeDrafts((current) => ({ ...current, [formId]: value }));
  };

  const runFormAction = async (
    formId: string,
    action: 'import' | 'reclassify',
  ) => {
    try {
      setErrorMessage('');
      const formType = formTypeDrafts[formId]?.trim();

      if (!formType) {
        setErrorMessage('Form type is required before import or reclassify.');
        return;
      }

      if (action === 'import') {
        const result = await importForm({ formId, formType }).unwrap();
        setNotice(
          `Import finished: ${result.imported} imported, ${result.skipped} skipped, ${result.failed} failed.`,
        );
      } else {
        const result = await reclassifyForm({ formId, formType }).unwrap();
        setNotice(
          `Reclassified ${result.reclassified} submissions to ${result.formType}.`,
        );
      }
    } catch (error) {
      console.error(error);
      setErrorMessage(`Could not ${action} this form.`);
    }
  };

  const handleImportAll = async () => {
    try {
      setErrorMessage('');
      const result = await importAllForms().unwrap();
      setNotice(
        `Imported all visible forms: ${result.total.imported} imported, ${result.total.skipped} skipped, ${result.total.failed} failed across ${result.forms} forms.`,
      );
    } catch (error) {
      console.error(error);
      setErrorMessage('Could not import all visible forms.');
    }
  };

  const handleSyncCatalog = async () => {
    try {
      setErrorMessage('');
      const result = await syncCatalog().unwrap();
      setNotice(
        `Catalog synced: ${result.formsSeen} seen, ${result.created} created, ${result.updated} updated, ${result.archived} archived.`,
      );
    } catch (error) {
      console.error(error);
      setErrorMessage('Could not sync the Typeform catalog.');
    }
  };

  const handleSyncDefinition = async (formId: string) => {
    try {
      setErrorMessage('');
      const result = await syncDefinition(formId).unwrap();
      setNotice(
        `Definition synced for ${result.formTitle ?? result.typeformFormId}.`,
      );
      setFormTypeDrafts((current) => ({
        ...current,
        [formId]: current[formId] ?? result.formType,
      }));
    } catch (error) {
      console.error(error);
      setErrorMessage('Could not sync this form definition.');
    }
  };

  const handleTogglePulseReportable = async (
    formId: string,
    pulseReportable: boolean,
  ) => {
    try {
      setErrorMessage('');
      const result = await updateCatalog({
        formId,
        form: { pulseReportable },
      }).unwrap();
      setNotice(
        `${result.form.formTitle ?? result.form.typeformFormId} ${
          result.form.pulseReportable ? 'enabled' : 'disabled'
        } for Pulse API reporting.`,
      );
    } catch (error) {
      console.error(error);
      setErrorMessage('Could not update pulse reporting for this form.');
    }
  };

  const submissionsTableRows = (() => {
    if (isSubmissionsLoading) {
      return (
        <TableRow>
          <TableCell colSpan={6}>Loading submissions…</TableCell>
        </TableRow>
      );
    }

    if (submissionsData?.submissions.length) {
      return submissionsData.submissions.map((submission) => (
        <TableRow key={submission.id}>
          <TableCell>
            <div className="flex flex-col">
              <span className="font-medium">
                {[submission.respondentFirstName, submission.respondentLastName]
                  .filter(Boolean)
                  .join(' ') || 'Unnamed respondent'}
              </span>
              <span className="text-xs text-cement-500">
                {submission.respondentEmail ?? 'No email mapped'}
              </span>
            </div>
          </TableCell>
          <TableCell>{submission.formType}</TableCell>
          <TableCell>
            {submission.respondentCompany ?? 'No company mapped'}
          </TableCell>
          <TableCell>
            <Chip
              color={statusColor(submission.status)}
              label={submission.status}
            />
          </TableCell>
          <TableCell>{formatDateTime(submission.submittedAt)}</TableCell>
          <TableCell>
            <Link
              className="no-underline"
              to={`/coach/manage/typeform/${submission.id}`}
            >
              <Button size="small" variant="outlined">
                Details
              </Button>
            </Link>
          </TableCell>
        </TableRow>
      ));
    }

    return (
      <TableRow>
        <TableCell colSpan={6}>
          No submissions match the current filters.
        </TableCell>
      </TableRow>
    );
  })();

  const pulseReportTableRows = (() => {
    if (isPulseReportsLoading) {
      return (
        <TableRow>
          <TableCell colSpan={6}>Loading pulse reports…</TableCell>
        </TableRow>
      );
    }

    if (pulseReportsData?.reports.length) {
      return pulseReportsData.reports.map((report) => (
        <TableRow key={report.id}>
          <TableCell>
            <div className="flex flex-col">
              <span className="font-medium">{report.memberName}</span>
              <span className="text-xs text-cement-500">
                {report.memberEmail ?? 'No member email'}
              </span>
            </div>
          </TableCell>
          <TableCell>{report.formType}</TableCell>
          <TableCell>
            <Chip
              color={report.status === 'hidden' ? 'warning' : 'success'}
              label={report.status}
            />
          </TableCell>
          <TableCell>{report.sourceCount}</TableCell>
          <TableCell>{formatDateTime(report.submittedAt)}</TableCell>
          <TableCell>
            <Link
              className="no-underline"
              to={`/coach/manage/typeform/pulse-reports/${report.id}`}
            >
              <Button size="small" variant="outlined">
                Details
              </Button>
            </Link>
          </TableCell>
        </TableRow>
      ));
    }

    return (
      <TableRow>
        <TableCell colSpan={6}>No pulse reports found.</TableCell>
      </TableRow>
    );
  })();

  let tabContent: ReactNode;

  if (activeTab === 'submissions') {
    tabContent = (
      <div className="mt-4 flex flex-col gap-4">
        <Card className="p-4">
          <Typography variant="h5">Submission filters</Typography>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-4">
            <TextField
              label="Search"
              value={filters.q}
              onChange={handleFilterChange('q')}
            />
            <TextField
              label="Form type"
              value={filters.formType}
              onChange={handleFilterChange('formType')}
            />
            <TextField
              label="Status"
              value={filters.status}
              onChange={handleFilterChange('status')}
            />
            <TextField
              label="Company"
              value={filters.company}
              onChange={handleFilterChange('company')}
            />
          </div>
          <div className="mt-4 flex gap-2">
            <Button onClick={handleFilterSubmit}>Apply filters</Button>
            <Button
              variant="outlined"
              onClick={() => {
                setFilters({
                  company: '',
                  formType: '',
                  q: '',
                  status: '',
                });
                setSearchParams(new URLSearchParams({ tab: 'submissions' }));
              }}
            >
              Clear
            </Button>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <Typography variant="h5">Submissions</Typography>
              <Typography variant="body2">
                Review incoming responses, inspect payloads, and link records to
                people.
              </Typography>
            </div>
            {isSubmissionsFetching && !isSubmissionsLoading ? (
              <Typography variant="body2">Refreshing…</Typography>
            ) : null}
          </div>

          <TableContainer className="mt-4">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Respondent</TableCell>
                  <TableCell>Form type</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{submissionsTableRows}</TableBody>
            </Table>
          </TableContainer>

          {submissionsData?.total ? (
            <Pagination
              count={formPageCount}
              page={submissionsData.page}
              onChange={handlePageChange}
            />
          ) : null}
        </Card>
      </div>
    );
  } else if (activeTab === 'pulseReports') {
    tabContent = (
      <div className="mt-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <Typography variant="h5">Pulse reports</Typography>
              <Typography variant="body2">
                Manage logical pulse report visibility, overrides, and attached
                correction sources.
              </Typography>
            </div>
            {isPulseReportsFetching && !isPulseReportsLoading ? (
              <Typography variant="body2">Refreshing…</Typography>
            ) : null}
          </div>

          <TableContainer className="mt-4">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Member</TableCell>
                  <TableCell>Form type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Sources</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{pulseReportTableRows}</TableBody>
            </Table>
          </TableContainer>
          {pulseReportsData?.total ? (
            <Pagination
              count={pulseReportPageCount}
              page={pulseReportsData.page}
              onChange={handlePageChange}
            />
          ) : null}
        </Card>
      </div>
    );
  } else {
    tabContent = (
      <div className="mt-4">
        <FormsPanel
          forms={formsData?.forms ?? []}
          formTypeDrafts={formTypeDrafts}
          isImporting={isImporting}
          isImportingAll={isImportingAll}
          isLoading={isFormsLoading || isFormsFetching}
          isReclassifying={isReclassifying}
          isSyncingCatalog={isSyncingCatalog}
          isSyncingDefinition={isSyncingDefinition}
          isUpdatingCatalog={isUpdatingCatalog}
          onDraftChange={handleDraftChange}
          onImport={(formId) => runFormAction(formId, 'import')}
          onImportAll={handleImportAll}
          onReclassify={(formId) => runFormAction(formId, 'reclassify')}
          onSyncCatalog={handleSyncCatalog}
          onSyncDefinition={handleSyncDefinition}
          onTogglePulseReportable={handleTogglePulseReportable}
        />
      </div>
    );
  }

  if (isUserLoading) {
    return (
      <div className="container mx-auto max-w-screen-xl p-4">
        <Typography variant="body1">
          Loading Typeform Interpretation…
        </Typography>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-screen-xl p-4">
      <PageHeaderLayout
        subtitle="Admin tooling for reviewing submissions, fixing mappings, and classifying Typeform forms."
        title="Typeform Interpretation"
      >
        {activeTab === 'submissions' ? (
          <Button
            disabled={isExporting}
            onClick={() => {
              handleExport();
            }}
          >
            Export CSV
          </Button>
        ) : null}
      </PageHeaderLayout>

      {notice ? (
        <div className="mt-4 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
          {notice}
        </div>
      ) : null}
      {errorMessage ? (
        <div className="mt-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {errorMessage}
        </div>
      ) : null}

      <div className="mt-6">
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Submissions" value="submissions" />
          <Tab label="Pulse reports" value="pulseReports" />
          <Tab label="Forms" value="forms" />
        </Tabs>
      </div>
      {tabContent}
    </div>
  );
};

export default TypeformAdminPage;
